import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { useId } from 'react';
import './components.css';

interface FieldBase {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement>, FieldBase {}
export function Input({ label, hint, error, id, className = '', ...rest }: InputProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <div className={`k-field ${error ? 'k-field--error' : ''} ${className}`}>
      {label && <label className="k-field__label" htmlFor={inputId}>{label}</label>}
      <input id={inputId} className="k-input" aria-invalid={!!error} {...rest} />
      {error ? <div className="k-field__error">{error}</div> : hint ? <div className="k-field__hint">{hint}</div> : null}
    </div>
  );
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, FieldBase {}
export function Textarea({ label, hint, error, id, className = '', ...rest }: TextareaProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <div className={`k-field ${error ? 'k-field--error' : ''} ${className}`}>
      {label && <label className="k-field__label" htmlFor={inputId}>{label}</label>}
      <textarea id={inputId} className="k-input k-textarea" aria-invalid={!!error} {...rest} />
      {error ? <div className="k-field__error">{error}</div> : hint ? <div className="k-field__hint">{hint}</div> : null}
    </div>
  );
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement>, FieldBase {}
export function Select({ label, hint, error, id, className = '', children, ...rest }: SelectProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <div className={`k-field ${error ? 'k-field--error' : ''} ${className}`}>
      {label && <label className="k-field__label" htmlFor={inputId}>{label}</label>}
      <select id={inputId} className="k-input k-select" aria-invalid={!!error} {...rest}>{children}</select>
      {error ? <div className="k-field__error">{error}</div> : hint ? <div className="k-field__hint">{hint}</div> : null}
    </div>
  );
}

export interface SwitchProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}
export function Switch({ checked, onChange, label, description, disabled }: SwitchProps) {
  return (
    <label className={`k-switch ${disabled ? 'k-switch--disabled' : ''}`}>
      <span className="k-switch__text">
        {label && <span className="k-switch__label">{label}</span>}
        {description && <span className="k-switch__desc">{description}</span>}
      </span>
      <span className="k-switch__control">
        <input type="checkbox" role="switch" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
        <span className="k-switch__track"><span className="k-switch__thumb" /></span>
      </span>
    </label>
  );
}
